import { IconButton } from '@ui/shared/Button';
import orderBy from 'lodash/sortBy';
import * as React from 'react';
import styled from 'styled-components';
import {
  IBlockData,
  IBlockReportState,
  IBlockResult,
} from '../../../data/providers/reports/blockReportProvider';
import { Entity } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardHeader } from '../../shared/card/CardHeader';
import { Icon } from '../../shared/Icon';
import { ListHeader } from '../../shared/List/ListHeader';
import { EPdfMode, isPdf } from '../../shared/pdf/Pdf';
import { BlockResult } from './BlockResult';

interface IProps {
  reportState: IBlockReportState;
  pdfMode: EPdfMode;
  entity: Entity;
  rawValsView: boolean;
  onRawValsViewChange: (v: boolean) => void;
}

interface ISort {
  sortKey: string;
  direction: 'asc' | 'desc';
}

export const ResultList = (props: IProps): React.ReactElement => {
  const [sort, setSort] = React.useState<ISort>({
    // Identifies the hash key from the first selected metric, which sorts the result list on the first column
    sortKey: Object.keys(props.reportState.blockResult[0].hashKeyVals)[0],
    direction: 'desc',
  });
  const { rawValsView, onRawValsViewChange } = props;

  // Keyboard shortcut for toggling raw/percentage view
  React.useEffect((): (() => void) => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // 'r' key toggles raw values view
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Don't trigger if user is typing in an input
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        onRawValsViewChange(!rawValsView);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return (): void => window.removeEventListener('keydown', handleKeyDown);
  }, [rawValsView, onRawValsViewChange]);

  const data = React.useMemo((): IBlockResult[] => {
    // Will initially order the result list descending by the first column
    return orderBy(props.reportState.blockResult, [
      (item: IBlockResult): boolean => item.hashKeyVals[sort.sortKey] === null,
      (item: IBlockResult): number => {
        return (
          item.hashKeyVals[sort.sortKey] * (sort.direction === 'desc' ? -1 : 1)
        );
      },
    ]);
  }, [props.reportState.blockResult, sort]);

  /**
   * @description handles sorting of the data based on the selected hash
   * @param hashKey
   */
  const onHashSort = (hashKey: string): void => {
    const newSortDir = sort?.direction === 'desc' ? 'asc' : 'desc';

    if (hashKey === sort.sortKey) {
      setSort(
        (sortState: ISort): ISort => ({ ...sortState, direction: newSortDir })
      );
    } else {
      setSort({ sortKey: `${hashKey}`, direction: 'asc' });
    }
  };

  /**
   * @description creates the result list header labels for each selected metric
   */
  const makeMetricLabels = (): React.ReactElement[] => {
    return data[0].data.map(
      (datum: IBlockData, index: number): React.ReactElement => {
        const hashKey = `${datum.hash + datum.metric.value}`;
        return (
          <MetricLabel
            direction={datum.metric.direction}
            key={index}
            id={hashKey}
            testMeta={datum.testType}
            metricLabel={datum.metric}
            metricUnits={datum.metric.units}
            onSort={(): void => onHashSort(hashKey)}
            sort={sort}
            pdfMode={props.pdfMode}
          />
        );
      }
    );
  };

  return (
    <Card
      style={{ marginTop: 'var(--grid-gap)' }}
      inverse={props.pdfMode === EPdfMode.PRINT}
      noBottomRadius
    >
      <Column>
        <CardHeader surface={6} withListHeader>
          <Column>
            {!isPdf(props.pdfMode) && (
              <HeaderControls>
                <ToggleButton
                  onClick={(): void => onRawValsViewChange(!rawValsView)}
                  title={`Show ${rawValsView ? 'percentages' : 'raw values'} (R)`}
                  active={rawValsView}
                >
                  <Icon name={rawValsView ? 'percent' : 'tag'} />
                  <span>{rawValsView ? 'Show %' : 'Show Raw'}</span>
                </ToggleButton>
              </HeaderControls>
            )}
            <ListHeader
              leftAlignLastCell
              labels={['Athlete', ...makeMetricLabels()]}
              shape={[1.5, ...data[0].data.map(() => 1)]}
            />
          </Column>
        </CardHeader>
        <CardBody notScrollable={isPdf(props.pdfMode)}>
          <BlockResult
            blockResult={data}
            pdfMode={props.pdfMode}
            range={props.reportState.blockTimeframe}
            rawValsView={rawValsView}
          />
        </CardBody>
      </Column>
    </Card>
  );
};

interface IMetricLabelProps {
  direction?: number;
  id?: string;
  metricLabel: { label: string; value: string };
  sort?: ISort;
  testMeta: string;
  metricUnits: string;
  onSort?: () => void;
  pdfMode: EPdfMode;
}

const MetricLabel = (props: IMetricLabelProps): React.ReactElement => {
  return (
    <Row style={{ minWidth: 0 }}>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}>
        <div>{props.testMeta}</div>
        <StyledLabel>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {props.metricLabel.label}{' '}
            {props.metricUnits ? `(${props.metricUnits})` : ''}
            {props.direction === -1 && <Icon name='south' />}
          </div>
        </StyledLabel>
      </div>
      {!isPdf(props.pdfMode) && (
        <div
          style={{
            flex: 0,
            marginLeft: 'auto',
            paddingRight: '10px',
            alignSelf: 'flex-end',
          }}
        >
          {props.id === props.sort.sortKey ? (
            <IconButton
              title={props.sort?.direction === 'asc' ? 'Sort Desc' : 'Sort Asc'}
              icon={
                props.sort?.direction === 'desc' ? 'expand_more' : 'expand_less'
              }
              onClick={props.onSort}
            />
          ) : (
            <IconButton title={'Sort'} icon={'sort'} onClick={props.onSort} />
          )}
        </div>
      )}
    </Row>
  );
};

const StyledLabel = styled.div`
  @media (max-width: 768px) {
    font-size: 0.8em;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 10px;
  border-bottom: 1px solid var(--color-separator);
`;

const ToggleButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--color-separator);
  border-radius: 6px;
  background: ${(props: { active?: boolean }): string =>
    props.active ? 'var(--color-primary)' : 'var(--color-surface-3)'};
  color: var(--color-text);
  cursor: pointer;
  font-size: 0.85em;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props: { active?: boolean }): string =>
      props.active ? 'var(--color-primary)' : 'var(--color-surface-6)'};
  }

  span {
    @media (max-width: 600px) {
      display: none;
    }
  }
`;
